import React, { useState, useEffect } from 'react';
import { Mail, MessageSquare, AlertCircle, CheckCircle, ShieldCheck } from 'lucide-react';
import SEO from '../components/SEO';

// Obfuscated Email Component to prevent scraping
const SafeMail: React.FC<{ user: string; domain: string; label?: string; className?: string }> = ({ user, domain, label, className }) => {
  const [email, setEmail] = useState('Click to reveal');
  
  const handleHover = () => {
    setEmail(`${user}@${domain}`);
  };

  return (
    <a 
      href={`mailto:${user}@${domain}`}
      onMouseEnter={handleHover}
      onFocus={handleHover}
      className={className}
    >
      {label || email}
    </a>
  );
};

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    captchaInput: '',
    honeypot: '' // Hidden field for bots
  });

  const [captcha, setCaptcha] = useState({ num1: 0, num2: 0 });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Generate simple math CAPTCHA on mount
  useEffect(() => {
    setCaptcha({
      num1: Math.floor(Math.random() * 10) + 1,
      num2: Math.floor(Math.random() * 10) + 1
    });
  }, []);

  const validate = () => {
    const newErrors: { [key: string]: string } = {};
    if (!formData.name.trim()) newErrors.name = 'Nama wajib diisi';
    if (!formData.email.trim()) newErrors.email = 'Email wajib diisi';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Format email tidak valid';
    if (!formData.message.trim()) newErrors.message = 'Pesan tidak boleh kosong';
    
    // CAPTCHA Check
    const expectedSum = captcha.num1 + captcha.num2;
    if (parseInt(formData.captchaInput) !== expectedSum) {
        newErrors.captcha = 'Jawaban matematika salah';
    }

    return newErrors;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // 1. Honeypot Check (Anti-Bot)
    if (formData.honeypot) {
        console.log("Bot detected");
        return; // Silent fail for bots
    }

    // 2. Validation
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
    }

    // 3. Construct Email (Client-Side Send)
    const subject = `[XCape Support Request] from ${formData.name}`;
    const body = `Name: ${formData.name}%0D%0AEmail: ${formData.email}%0D%0A%0D%0AMessage:%0D%0A${formData.message}`;
    
    // Open default mail client
    window.location.href = `mailto:support@xcape.my.id?subject=${subject}&body=${body}`;
    
    // Show UI Feedback
    setStatus('success');
    setFormData({ name: '', email: '', message: '', captchaInput: '', honeypot: '' });
    setErrors({});
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user types
    if (errors[name]) {
        setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <div className="pt-24 pb-16">
       <SEO title="Hubungi Kami - Support XCape" description="Hubungi tim support XCape untuk bantuan teknis atau request aplikasi." />
       <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
            <h1 className="text-4xl font-bold text-slate-900 mb-4">Hubungi Kami</h1>
            <p className="text-slate-500">Punya pertanyaan atau ingin request aplikasi khusus?</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
                {/* Telegram Card */}
                <div className="bg-white p-8 rounded-2xl border border-slate-200 mb-6 shadow-sm hover:border-brand-primary transition-colors">
                    <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center">
                        <MessageSquare className="h-6 w-6 mr-3 text-brand-primary"/>
                        Telegram Support
                    </h3>
                    <p className="text-slate-500 mb-4 text-sm">
                        Bergabunglah dengan channel Telegram kami untuk update status server real-time dan diskusi komunitas.
                    </p>
                    <a 
                        href="https://t.me/XCapeOfficial_ID" 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="text-brand-primary font-bold hover:underline flex items-center gap-2"
                    >
                        @XCapeOfficial_ID
                    </a>
                </div>

                {/* Secure Email Card */}
                <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm hover:border-brand-primary transition-colors">
                     <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center">
                        <Mail className="h-6 w-6 mr-3 text-brand-primary"/>
                        Email Enkripsi
                    </h3>
                    <p className="text-slate-500 mb-4 text-sm">
                        Untuk pertanyaan bisnis atau keamanan tingkat lanjut (Enterprise).
                    </p>
                    <SafeMail 
                        user="secure" 
                        domain="xcape.my.id" 
                        className="text-brand-primary font-bold hover:underline break-all"
                    />
                </div>
            </div>

            {/* Contact Form */}
            <form onSubmit={handleSubmit} className="bg-white p-8 rounded-2xl border border-slate-200 shadow-xl relative overflow-hidden">
                {status === 'success' && (
                    <div className="mb-6 p-4 bg-green-50 text-green-700 rounded-lg flex items-center gap-2 border border-green-200 animate-fade-in-up">
                        <CheckCircle className="h-5 w-5" />
                        <span>Aplikasi email Anda telah terbuka. Silakan klik kirim!</span>
                    </div>
                )}

                {/* Honeypot Field (Hidden) */}
                <input 
                    type="text" 
                    name="honeypot" 
                    value={formData.honeypot} 
                    onChange={handleChange} 
                    className="hidden" 
                    autoComplete="off"
                />

                <div className="mb-6">
                    <label className="block text-slate-700 text-sm font-bold mb-2">Nama</label>
                    <input 
                        type="text" 
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className={`w-full bg-slate-50 border rounded-lg py-3 px-4 text-slate-900 focus:outline-none focus:ring-1 focus:ring-brand-primary ${errors.name ? 'border-red-500' : 'border-slate-200 focus:border-brand-primary'}`} 
                        placeholder="Nama Anda" 
                    />
                    {errors.name && <p className="text-red-500 text-xs mt-1 flex items-center"><AlertCircle className="w-3 h-3 mr-1"/>{errors.name}</p>}
                </div>

                <div className="mb-6">
                    <label className="block text-slate-700 text-sm font-bold mb-2">Email</label>
                    <input 
                        type="email" 
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`w-full bg-slate-50 border rounded-lg py-3 px-4 text-slate-900 focus:outline-none focus:ring-1 focus:ring-brand-primary ${errors.email ? 'border-red-500' : 'border-slate-200 focus:border-brand-primary'}`}
                        placeholder="email@anda.com" 
                    />
                    {errors.email && <p className="text-red-500 text-xs mt-1 flex items-center"><AlertCircle className="w-3 h-3 mr-1"/>{errors.email}</p>}
                </div>

                 <div className="mb-6">
                    <label className="block text-slate-700 text-sm font-bold mb-2">Pesan / Request Aplikasi</label>
                    <textarea 
                        rows={4} 
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        className={`w-full bg-slate-50 border rounded-lg py-3 px-4 text-slate-900 focus:outline-none focus:ring-1 focus:ring-brand-primary ${errors.message ? 'border-red-500' : 'border-slate-200 focus:border-brand-primary'}`}
                        placeholder="Tulis pesan Anda disini..."
                    ></textarea>
                    {errors.message && <p className="text-red-500 text-xs mt-1 flex items-center"><AlertCircle className="w-3 h-3 mr-1"/>{errors.message}</p>}
                </div>

                {/* Math CAPTCHA */}
                <div className="mb-8 p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <label className="block text-slate-700 text-sm font-bold mb-2 flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4 text-brand-primary"/> Keamanan: Berapa hasil dari {captcha.num1} + {captcha.num2}?
                    </label>
                    <input 
                        type="number" 
                        name="captchaInput"
                        value={formData.captchaInput}
                        onChange={handleChange}
                        className={`w-24 bg-white border rounded-lg py-2 px-3 text-slate-900 focus:outline-none focus:ring-1 focus:ring-brand-primary ${errors.captcha ? 'border-red-500' : 'border-slate-300 focus:border-brand-primary'}`}
                        placeholder="?" 
                    />
                    {errors.captcha && <p className="text-red-500 text-xs mt-1 flex items-center"><AlertCircle className="w-3 h-3 mr-1"/>{errors.captcha}</p>}
                </div>

                <button 
                    type="submit" 
                    className="w-full bg-brand-primary hover:bg-orange-600 text-white font-bold py-3 rounded-lg transition-colors shadow-lg shadow-orange-500/20 active:transform active:scale-95"
                >
                    Kirim Pesan
                </button>
                <p className="text-center text-xs text-slate-400 mt-4">
                    Pesan akan dikirim ke support@xcape.my.id melalui aplikasi email default Anda.
                </p>
            </form>
        </div>
       </div>
    </div>
  );
};

export default Contact;